import { defineComponent as _defineComponent } from 'vue'
import WidgetDefinition, {
  AddWidgetPayload,
} from '@/models/Charts/widgetDefinition';
import { Device } from '@/models/device';
import { ConsoleEnum } from '@/models/enums/ConsoleEnum';
import { FilterTimeSpanEnum } from '@/models/enums/FilterTimeSpanEnum';
import { WidgetEnum } from '@/models/enums/WidgetEnum';
import { Logger } from '@/utils/logger';
import { isEmpty, isNil } from '@/utils/misc';
import { LocaleMessage } from 'vue-i18n';
import WidgetContainer from './widgetContainer/WidgetContainer.vue';
import { devicesService } from '@/services/devices.service';
import { usersService } from '@/services/users.service';
import { customersService } from '@/services/tenants.service';
import {
  LayoutSlot,
  TOTAL_COLUMNS,
  TOTAL_ROWS,
} from '@/views/dashboard/layout-grid';
import { computed, ref } from 'vue';
import { useI18n } from '@/composables/i18n';
import { currentUser } from '@/utils/permissionUtils';
import { BetaFeature } from '@/models/enums/BetaFeature';
import { MachineType } from '@/models/enums/MachineType';

type GroupedWidgets = Record<string, WidgetEnum[]>;
type GroupedWidgetDefinitions = Record<string, WidgetDefinition[]>;


export default /*#__PURE__*/_defineComponent({
  __name: 'WidgetSelector',
  props: {
    selectedSlot: null
  },
  emits: ["close", "add-widget"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'close'): void;
  (e: 'add-widget', value: AddWidgetPayload): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  selectedSlot: LayoutSlot;
};





const { t } = useI18n();

const userConsoles = computed<ConsoleEnum[]>(() => {
  const currentUser = usersService.store.current();
  const currentCustomer = customersService.store.current()!;
  const consoles: ConsoleEnum[] = [];
  if (currentCustomer.hasDatahub) {
    if (
      currentUser.consoles.includes(ConsoleEnum.WCC) ||
      currentUser.isGlobalAdmin
    ) {
      consoles.push(ConsoleEnum.WCC);
    }
  }
  if (currentCustomer.hasSmbs) {
    if (
      currentUser.consoles.includes(ConsoleEnum.BC) ||
      currentUser.isGlobalAdmin
    ) {
      consoles.push(ConsoleEnum.BC);
    }
    if (
      currentUser.consoles.includes(ConsoleEnum.SFC) ||
      currentUser.isGlobalAdmin
    ) {
      consoles.push(ConsoleEnum.SFC);
    }
  }
  return consoles;
});

const selectedConsole = ref<ConsoleEnum>(userConsoles.value[0]);
const selectedDeviceStringId = ref<string | null>(
  getDefaultConsoleDeviceStringId(selectedConsole.value),
);

const isSpecificDeviceNotOverview = computed<boolean>(
  () => selectedDeviceStringId.value?.substring(2) !== 'overview',
);

const wccCutWidgets = computed<GroupedWidgets>(() => {
  const commonWidgets = {
    '': [WidgetEnum.CutStatus],
    'report.comparative_data': [
      WidgetEnum.LaserAvailability,
      WidgetEnum.LaserProductivity,
      WidgetEnum.LaserOee,
      WidgetEnum.LaserStarveBlock,
      WidgetEnum.LaserOutputScrap,
      WidgetEnum.LaserCuttingTime,
    ],
    'report.historic_data': [
      WidgetEnum.LaserAvailabilityHist,
      WidgetEnum.LaserProductivityHist,
      WidgetEnum.LaserOeeHist,
      WidgetEnum.LaserCuttingTimeHist,
      WidgetEnum.LaserStarveBlockHist,
      WidgetEnum.LaserOutputAndScrapTimeline,
    ],
  };
  if (selectedDeviceStringId.value === 'L-overview') {
    return {
      ...commonWidgets,
      '': [...commonWidgets[''], WidgetEnum.LaserIntradayAvailabilityTable],
    };
  } else {
    return {
      ...commonWidgets,
      '': [...commonWidgets[''], WidgetEnum.CameraView],
    };
  }
});

const wccBendWidgets = computed<GroupedWidgets>(() => {
  if (isNewBendConsoleEnabled.value) {
    return {
      '': [WidgetEnum.CameraView],
    };
  } else {
    const commonWidgets = {
      '': [WidgetEnum.BendStatus],
      'report.comparative_data': [
        WidgetEnum.BendingAvailability,
        WidgetEnum.BendingTimeBetweenBend,
        WidgetEnum.BendingPerformancePart,
        WidgetEnum.BendingTopParts,
      ],
      'report.historic_data': [
        WidgetEnum.BendingAvailabilityHist,
        WidgetEnum.BendingTimeBetweenBendHist,
        WidgetEnum.BendingPerformancePartHist,
        WidgetEnum.BendingPerformanceHist,
        WidgetEnum.BendingNumberOfBendsTimeline,
      ],
    };

    if (selectedDeviceStringId.value === 'B-overview') {
      return commonWidgets;
    } else {
      return {
        ...commonWidgets,
        '': [...commonWidgets[''], WidgetEnum.CameraView],
      };
    }
  }
});

const wccTubeWidgets = computed<GroupedWidgets>(() => {
  const commonWidgets = {
    '': [WidgetEnum.TubeStatus],
    'report.comparative_data': [
      WidgetEnum.TubeAvailability,
      WidgetEnum.TubeProductivity,
      WidgetEnum.TubeOee,
      WidgetEnum.TubeStarveBlock,
      WidgetEnum.TubeOutputScrap,
      WidgetEnum.TubeCuttingTime,
    ],
    'report.historic_data': [
      WidgetEnum.TubeAvailabilityHist,
      WidgetEnum.TubeProductivityHist,
      WidgetEnum.TubeOeeHist,
      WidgetEnum.TubeCuttingTimeHist,
      WidgetEnum.TubeStarveBlockHist,
      WidgetEnum.TubeOutputScrapHist,
    ],
  };

  if (selectedDeviceStringId.value === 'T-overview') {
    return {
      ...commonWidgets,
      '': [...commonWidgets[''], WidgetEnum.TubeIntradayAvailabilityTable],
    };
  } else {
    return {
      ...commonWidgets,
      '': [...commonWidgets[''], WidgetEnum.CameraView],
    };
  }
});

const wccWidgets = computed<GroupedWidgets>(() => {
  if (isNil(selectedDeviceStringId.value)) {
    return { '': [] };
  }

  switch (Device.GetMachineTypeById(selectedDeviceStringId.value)) {
    case MachineType.FactoryOverview:
      return { '': [WidgetEnum.FactoryOverview] };
    case MachineType.Cut:
      return wccCutWidgets.value;
    case MachineType.Bend:
      return wccBendWidgets.value;
    case MachineType.Tube:
      return wccTubeWidgets.value;
    case MachineType.Undefined:
      Logger.error('Unknown device type found');
      return { '': [] };
  }
});

const bcWidgets: GroupedWidgets = {
  'report.overview': [
    WidgetEnum.SalesMap,
    WidgetEnum.QuotesMap,
    WidgetEnum.OpenSalesFunnel,
    WidgetEnum.BusinessOverviewFigures,
  ],
  'report.quotes': [
    WidgetEnum.QuotesAbcCustomers,
    WidgetEnum.QuotesConvertedAmountTimeline,
    WidgetEnum.QuotesConversionRates,
    WidgetEnum.QuotesSize,
    WidgetEnum.QuotesFigures,
    WidgetEnum.QuotesTopQuotedMaterialsTimeline,
    WidgetEnum.QuotesQuotedMaterialsList,
    WidgetEnum.QuotesConvertedMap,
  ],
  'report.sales': [
    WidgetEnum.SalesCustomerGroups,
    WidgetEnum.SalesTopSalespeople,
    WidgetEnum.SalesFunnel,
    WidgetEnum.SalesFigures,
    WidgetEnum.SalesTimeline,
    WidgetEnum.SalesUnpaidDistribution,
  ],
};

const sfcWidgets: GroupedWidgets = {
  'report.manufacturing': [
    WidgetEnum.ManufacturingPOStatus,
    WidgetEnum.ManufacturingFinishedPOEstimatedVsActualPOL,
    WidgetEnum.ManufacturingReleasedPOStatus,
    WidgetEnum.ManufacturingPOLStatusDueDate,
    WidgetEnum.ManufacturingFigures,
    WidgetEnum.ManufacturingPOFinishedDueDate,
  ],
};

const widgets = computed<GroupedWidgets>(() => {
  switch (selectedConsole.value) {
    case ConsoleEnum.WCC:
      return wccWidgets.value;
    case ConsoleEnum.BC:
      return bcWidgets;
    case ConsoleEnum.SFC:
      return sfcWidgets;
    default:
      Logger.error(
        'Unknown console received in Widget Selector',
        selectedConsole.value,
      );
      return { '': [] };
  }
});

const widgetDefinitions = computed<GroupedWidgetDefinitions>(() =>
  Object.fromEntries(
    Object.entries(widgets.value).map(([label, widgets]) => [
      label,
      widgets.map((widget) => {
        return new WidgetDefinition(
          widget,
          isSpecificDeviceNotOverview.value
            ? [selectedDeviceStringId.value!]
            : [],
          FilterTimeSpanEnum.None,
          [],
        );
      }),
    ]),
  ),
);

const selectedWidgetDefinition = ref<WidgetDefinition>(
  Object.values(widgetDefinitions.value)[0][0],
);
const laserDevices = computed<Device[]>(() =>
  devicesService.store.getAllCutDevices(),
);

const bendingDevices = computed<Device[]>(() =>
  devicesService.store.getAllBendDevices(),
);

const tubesDevices = computed<Device[]>(() =>
  devicesService.store.getAllTubeDevices(),
);

const hasLaser = computed<boolean>(() => !isEmpty(laserDevices.value));

const hasBending = computed<boolean>(() => !isEmpty(bendingDevices.value));

const hasTubes = computed<boolean>(() => !isEmpty(tubesDevices.value));

const isNewBendConsoleEnabled = computed<boolean>(() =>
  currentUser().hasBetaFeature(BetaFeature.WorkCenterConsoleBend),
);

const slotAllowsBigWidget = computed<boolean>(
  () =>
    props.selectedSlot.height === TOTAL_ROWS &&
    props.selectedSlot.width >= TOTAL_COLUMNS / 2,
);

const showFactoryOverviewWarning = computed<boolean>(
  () =>
    !slotAllowsBigWidget.value &&
    selectedWidgetDefinition.value.widget === WidgetEnum.FactoryOverview,
);

const showGanttChartWarning = computed<boolean>(
  () =>
    !slotAllowsBigWidget.value &&
    (selectedWidgetDefinition.value.widget ===
      WidgetEnum.LaserIntradayAvailabilityTable ||
      selectedWidgetDefinition.value.widget ===
        WidgetEnum.TubeIntradayAvailabilityTable),
);

const isAcceptButtonDisabled = computed<boolean>(
  () => showFactoryOverviewWarning.value || showGanttChartWarning.value,
);

function onConsoleSelected(console: ConsoleEnum) {
  selectedConsole.value = console;
  selectedDeviceStringId.value = getDefaultConsoleDeviceStringId(
    selectedConsole.value,
  );
  selectedWidgetDefinition.value = Object.values(widgetDefinitions.value)[0][0];
}

function onDeviceSelected(deviceStringId: string) {
  selectedDeviceStringId.value = deviceStringId;
  selectedWidgetDefinition.value = Object.values(widgetDefinitions.value)[0][0];
}

function onWidgetSelected(wd: WidgetDefinition) {
  selectedWidgetDefinition.value = wd;
}

function getDefaultConsoleDeviceStringId(console: ConsoleEnum): string | null {
  switch (console) {
    case ConsoleEnum.WCC:
      return 'F-overview';
    default:
      return null;
  }
}

function onAddWidget() {
  const widget = selectedWidgetDefinition.value.widget;
  if (
    (widget === WidgetEnum.FactoryOverview ||
      widget === WidgetEnum.LaserIntradayAvailabilityTable ||
      widget === WidgetEnum.TubeIntradayAvailabilityTable) &&
    !slotAllowsBigWidget.value
  ) {
    return;
  }
  if (selectedConsole.value === ConsoleEnum.WCC) {
    emit('add-widget', {
      widget,
      deviceId: isSpecificDeviceNotOverview.value
        ? selectedDeviceStringId.value!
        : '',
    });
  } else {
    emit('add-widget', {
      widget,
    });
  }
  emit('close');
}

function consoleName(console: ConsoleEnum): LocaleMessage {
  switch (console) {
    case ConsoleEnum.BC:
      return t('category_menu.BC');
    case ConsoleEnum.SFC:
      return t('category_menu.SFC');
    case ConsoleEnum.WCC:
      return t('category_menu.WCC');
    default:
      return '';
  }
}

return { __sfc: true,props, emit, t, userConsoles, selectedConsole, selectedDeviceStringId, isSpecificDeviceNotOverview, wccCutWidgets, wccBendWidgets, wccTubeWidgets, wccWidgets, bcWidgets, sfcWidgets, widgets, widgetDefinitions, selectedWidgetDefinition, laserDevices, bendingDevices, tubesDevices, hasLaser, hasBending, hasTubes, isNewBendConsoleEnabled, slotAllowsBigWidget, showFactoryOverviewWarning, showGanttChartWarning, isAcceptButtonDisabled, onConsoleSelected, onDeviceSelected, onWidgetSelected, getDefaultConsoleDeviceStringId, onAddWidget, consoleName, ConsoleEnum, WidgetContainer }
}

})